import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getSubscriptionResults, pauseSubscription, postCancelIntent } from 'API/subscription';
import { Routes } from 'enums';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { CANCEL_REASONS, type CancelOffer, type CancelReason, type SubscriptionResults } from 'types/engagement.types';
import { fmtDec, fmtInt } from '../shared/format';

interface Props {
  open: boolean;
  onClose(): void;
  /** Continue to the existing cancel path (Stripe customer portal). Always reachable. */
  onCancel(): void;
  cancelling?: boolean;
}

const monthsOf = (r?: SubscriptionResults): number | undefined => {
  const p = r?.period;
  return p && typeof p === 'object' && typeof p.months === 'number' ? p.months : undefined;
};

const ResultTile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ flex: '1 1 100px', border: '1px solid', borderColor: 'greys.lighter', borderRadius: 2, p: 1.5 }}>
    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
    <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export interface CancelDialogViewProps {
  results?: SubscriptionResults;
  resultsLoading: boolean;
  resultsError: boolean;
  reason: CancelReason | null;
  comment: string;
  offer?: CancelOffer;
  offerLoading: boolean;
  pauseMonths: 1 | 2 | 3;
  pauseState: 'idle' | 'pending' | 'done' | 'error';
  cancelling?: boolean;
  onReason(r: CancelReason): void;
  onComment(c: string): void;
  onPauseMonths(m: 1 | 2 | 3): void;
  onOffer(): void;
  onCancel(): void;
  onClose(): void;
}

/**
 * Presentational part (testable without DOM). The "Kündigen" button is rendered in every state —
 * before a reason, while the offer loads, after the offer — and is never disabled by the flow.
 */
export const CancelDialogView: React.FC<CancelDialogViewProps> = ({
  results,
  resultsLoading,
  resultsError,
  reason,
  comment,
  offer,
  offerLoading,
  pauseMonths,
  pauseState,
  cancelling,
  onReason,
  onComment,
  onPauseMonths,
  onOffer,
  onCancel,
  onClose,
}) => {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const months = monthsOf(results);
  const offerLabel =
    offer?.kind === 'pause'
      ? t('engCancelPauseButton', { count: pauseMonths })
      : offer?.kind === 'downgrade'
        ? t('engCancelDowngradeButton')
        : offer?.kind === 'review'
          ? t('engCancelReviewButton')
          : null;

  return (
    <>
      <DialogTitle sx={{ pr: 6 }}>
        {months ? t('engCancelTitleMonths', { count: months }) : t('engCancelTitle')}
        <IconButton aria-label={t('engClose')} onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          {resultsLoading ? <LinearProgress /> : null}
          {resultsError ? <Alert severity={'info'}>{t('engCancelResultsFailed')}</Alert> : null}
          {results ? (
            <>
              <Stack direction={'row'} gap={1.5} flexWrap={'wrap'} data-testid={'cancel-results'}>
                <ResultTile label={t('engResultConversations')} value={fmtInt(lng)(results.conversations)} />
                <ResultTile label={t('engResultReplies')} value={fmtInt(lng)(results.replies)} />
                <ResultTile label={t('engResultHoursSaved')} value={`${fmtDec(lng, results.hoursSaved % 1 === 0 ? 0 : 1)(results.hoursSaved)} h`} />
              </Stack>
              {results.calculation ? <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{results.calculation}</Typography> : null}
            </>
          ) : null}

          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14, mb: 1 }}>{t('engCancelWhy')}</Typography>
            <Stack direction={'row'} gap={1} flexWrap={'wrap'}>
              {CANCEL_REASONS.map((r) => (
                <Chip
                  key={r}
                  label={t(`engCancelReason_${r}`)}
                  color={reason === r ? 'primary' : 'default'}
                  variant={reason === r ? 'filled' : 'outlined'}
                  onClick={() => onReason(r)}
                />
              ))}
            </Stack>
            {reason === 'other' ? (
              <TextField
                size={'small'}
                fullWidth
                multiline
                minRows={2}
                sx={{ mt: 1.5 }}
                label={t('engCancelComment')}
                value={comment}
                onChange={(e) => onComment(e.target.value)}
              />
            ) : null}
          </Box>

          {offerLoading ? <LinearProgress /> : null}
          {offer && offer.kind !== 'none' ? (
            <Alert severity={'info'} icon={false} data-testid={'cancel-offer'}>
              <Typography sx={{ fontSize: 14 }}>{offer.text}</Typography>
              {offer.kind === 'pause' ? (
                <TextField
                  select
                  size={'small'}
                  sx={{ mt: 1.5, minWidth: 180 }}
                  label={t('engCancelPauseMonths')}
                  value={pauseMonths}
                  onChange={(e) => onPauseMonths(Number(e.target.value) as 1 | 2 | 3)}
                >
                  {[1, 2, 3].map((m) => (
                    <MenuItem key={m} value={m}>
                      {t('engMonths', { count: m })}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
            </Alert>
          ) : null}
          {pauseState === 'done' ? <Alert severity={'success'}>{t('engCancelPaused', { count: pauseMonths })}</Alert> : null}
          {pauseState === 'error' ? <Alert severity={'error'}>{t('engCancelPauseFailed')}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.5 }}>
        {offerLabel && pauseState !== 'done' ? (
          <Button variant={'contained'} size={'large'} sx={{ flex: 1 }} onClick={onOffer} disabled={pauseState === 'pending'} data-testid={'cancel-offer-button'}>
            {offerLabel}
          </Button>
        ) : null}
        {pauseState === 'done' ? (
          <Button variant={'contained'} size={'large'} sx={{ flex: 1 }} onClick={onClose}>
            {t('engClose')}
          </Button>
        ) : null}
        {/* Always visible, same size as the offer: leaving must stay as easy as staying. */}
        <Button variant={'outlined'} size={'large'} sx={{ flex: 1 }} onClick={onCancel} disabled={cancelling} data-testid={'cancel-confirm'}>
          {t('engCancelButton')}
        </Button>
      </DialogActions>
    </>
  );
};

/**
 * Shown before the Stripe portal when a customer wants to cancel: lifetime results, the reason,
 * an offer matching the reason (pause first for "Stelle besetzt"), and an equal "Kündigen".
 */
const CancelSubscriptionDialog: React.FC<Props> = ({ open, onClose, onCancel, cancelling }) => {
  const navigate = useNavigate();
  const [reason, setReason] = useState<CancelReason | null>(null);
  const [comment, setComment] = useState('');
  const [pauseMonths, setPauseMonths] = useState<1 | 2 | 3>(3);

  const results = useQuery({
    queryKey: ['subscription', 'results', 'lifetime'],
    queryFn: () => getSubscriptionResults('lifetime'),
    enabled: open,
    retry: false,
  });
  const intent = useMutation({ mutationFn: ({ r, c }: { r: CancelReason; c?: string }) => postCancelIntent(r, c) });
  const pause = useMutation({ mutationFn: (m: 1 | 2 | 3) => pauseSubscription(m) });

  const reset = () => {
    setReason(null);
    setComment('');
    intent.reset();
    pause.reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleReason = (r: CancelReason) => {
    setReason(r);
    // "Anderer Grund" waits for the optional comment until the offer button / Kündigen.
    if (r !== 'other') intent.mutate({ r });
    else intent.reset();
  };

  const handleOffer = () => {
    const offer = intent.data?.offer;
    if (!offer) return;
    if (offer.kind === 'pause') pause.mutate(pauseMonths);
    if (offer.kind === 'downgrade') {
      handleClose();
      navigate(Routes.UPGRADE);
    }
    if (offer.kind === 'review') {
      handleClose();
      navigate(Routes.AI_CHAT);
    }
  };

  const handleCancel = () => {
    // Record the reason if the comment path never sent it; never block leaving on it.
    if (reason === 'other' && !intent.data && !intent.isPending) intent.mutate({ r: 'other', c: comment });
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={'sm'} fullWidth>
      <CancelDialogView
        results={results.data}
        resultsLoading={results.isLoading && open}
        resultsError={!!results.error}
        reason={reason}
        comment={comment}
        offer={intent.data?.offer}
        offerLoading={intent.isPending}
        pauseMonths={pauseMonths}
        pauseState={pause.isPending ? 'pending' : pause.isSuccess ? 'done' : pause.isError ? 'error' : 'idle'}
        cancelling={cancelling}
        onReason={handleReason}
        onComment={(c) => setComment(c)}
        onPauseMonths={setPauseMonths}
        onOffer={handleOffer}
        onCancel={handleCancel}
        onClose={handleClose}
      />
    </Dialog>
  );
};

export default CancelSubscriptionDialog;
